import type { ReactElement, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { formatDateTime, formatRelativeTime } from '../../lib/format.js';
import { cn } from '@tailoring-crm/ui';

/**
 * The column every history in the app is drawn in: a dot per event, a line joining them, and the
 * event beside it. The drawing lives here and the phrasing stays with each timeline.
 *
 * What a PERSON did gets the full weight of a row; what the SYSTEM recorded reads quieter —
 * flattening the two would make "the stage changed to quoted" look as though somebody had done
 * something, when it is the residue of the call above it.
 *
 * Times are relative ("2 days ago") with the exact instant on the `title`, because the question
 * a history asks is "how long ago" and nobody counts that from a timestamp. Newest first — the
 * API sorts, this only renders.
 */

export interface TimelineRailRow {
  id: string;
  at: string;
  /** Recorded by the system rather than done by a person. Drawn quieter. */
  system: boolean;
  icon: LucideIcon;
  headline: ReactNode;
  actorName: string | null;
  /** The shop's own words. Never abbreviated — this is the line somebody opened the page for. */
  body: string | null;
  /** Anything else the row carries: a link to the record, where it came from. */
  aside?: ReactNode;
}

export interface TimelineRailProps {
  rows: readonly TimelineRailRow[];
  /** Shown instead of the column when there is nothing in it. */
  empty: ReactNode;
  /** Said under the list when it was cut, rather than implying that is all that ever happened. */
  truncatedNotice?: ReactNode;
}

export function TimelineRail({ rows, empty, truncatedNotice }: TimelineRailProps): ReactElement {
  if (rows.length === 0) {
    return (
      <p className="activity-empty rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        {empty}
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      <ol className="flex flex-col">
        {rows.map((row, index) => {
          const Icon = row.icon;
          return (
            <li key={row.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    'timeline-dot mt-1 flex size-7 shrink-0 items-center justify-center rounded-full',
                    row.system ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary',
                  )}
                >
                  <Icon aria-hidden="true" className="size-3.5" />
                </span>
                {index < rows.length - 1 ? (
                  <span aria-hidden="true" className="w-px flex-1 bg-border" />
                ) : null}
              </div>

              <div className={cn('min-w-0 flex-1 pb-5', row.system && 'text-muted-foreground')}>
                <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className={cn(!row.system && 'font-medium')}>{row.headline}</span>
                  <time
                    dateTime={row.at}
                    title={formatDateTime(row.at)}
                    className="text-xs text-muted-foreground"
                  >
                    {formatRelativeTime(row.at)}
                  </time>
                </p>

                {row.actorName || row.aside ? (
                  <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
                    {row.actorName ? <span>{row.actorName}</span> : null}
                    {row.aside}
                  </p>
                ) : null}

                {row.body ? (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">
                    {row.body}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {truncatedNotice ? (
        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
          {truncatedNotice}
        </p>
      ) : null}
    </div>
  );
}
